import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Box, IconButton, type SxProps, Typography } from "@mui/material";
import { DateTime } from "luxon";
import type React from "react";
import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useEffectiveWeekStartDay } from "../../hooks/useEffectiveWeekStartDay";
import { BADGE, INK } from "../../theme/tokens";
import { trackErrorEvent, trackEvent } from "../../utils/analytics";
import { getVisibleWeekOffset, getWeekStart } from "../../utils/dateUtils";
import { daySchedulePath, staffingMonthlyPath, staffingPeoplePath, staffingWeeklyPath } from "../../utils/staffingRoutes";
import { enumToDropdownItems } from "../../utils/utilFunctions";
import { SystemDropdown } from "../UIToolkit/Inputs/SystemDropDown";
import { SystemDatePickerPopup } from "../UIToolkit/Popups/SystemDatePickerPopup";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";
import { MONTHLY_WEEKS_TO_SHOW } from "./MonthlyView/jobView/useMonthlyJobViewState";
import { useMonthlyViewAvailable } from "./MonthlyView/jobView/useMonthlyViewAvailable";
import { mainElementsColor } from "./MonthlyView/utils";
import { useStaffingPeopleViewAvailable } from "./PeopleView/useStaffingPeopleViewAvailable";
import { useLatchedViewOffer } from "./viewAvailability";

export const headerTypographySx = {
	fontWeight: 500,
	fontSize: { xl: "20px", xs: "16px" },
	lineHeight: "100%",
	letterSpacing: "0%",
	color: mainElementsColor,
} as const satisfies SxProps;

enum StaffingPageType {
	DAILY = "Day",
	WEEKLY = "Week",
	MONTHLY = "Month",
	/** The People roster — a first-class view like the other three, and equally dated: it projects each
	    nurse's rotation onto real calendar weeks, so it pages through them with the same controls. */
	PEOPLE = "People",
}
type StaffingPageKeys = keyof typeof StaffingPageType;

type BaseProps = {
	currentDateText: string;
	onResetButtonClick: React.MouseEventHandler<HTMLButtonElement>;
	onNextClick?: React.MouseEventHandler<HTMLButtonElement>;
	onPreviousClick?: React.MouseEventHandler<HTMLButtonElement>;
	initialPageType: StaffingPageKeys;
	weekIndex?: number;
	setWeekIndex?: (index: number) => void;
	currentDailyDate?: DateTime;
	isPrevDisabled?: boolean;
	isNextDisabled?: boolean;
	/** Current date value for the date picker */
	currentDate?: DateTime;
	/** Callback when a date is selected from the picker. If not provided, date text is not clickable. */
	onDateSelected?: (date: DateTime) => void;
	/** Minimum selectable date in the picker */
	minDate?: DateTime;
	/** Maximum selectable date in the picker */
	maxDate?: DateTime;
	/**
	 * Last day the view currently has on screen. Only the multi-week views need it: leaving one for a
	 * dated view anchors on today when today is genuinely visible, and Monthly can derive its own end
	 * from `MONTHLY_WEEKS_TO_SHOW` while People's span is a department setting it has to report.
	 */
	visibleRangeEnd?: DateTime;
	/**
	 * Annie walkthrough anchor (`context/standards/frontend.md`). Explicit because this component doesn't
	 * spread rest props, and TypeScript exempts hyphenated JSX attributes from excess-property checking —
	 * so an unknown `data-*` prop is dropped silently rather than failing the build.
	 *
	 * Passed by the caller, not hardcoded here: the Day Schedule and the monthly view render this same
	 * selector, and an anchor on all three would spotlight it on pages the step's copy isn't about.
	 */
	"data-annie-target"?: string;
};
type DailyProps = BaseProps & {
	weekIndex?: never;
	setWeekIndex: (index: number) => void;
	initialPageType: Extract<StaffingPageKeys, "DAILY">;
	currentDailyDate: DateTime;
};
type WeeklyOrMonthlyProps = BaseProps & {
	weekIndex: number;
	setWeekIndex: (index: number) => void;
	initialPageType: Extract<StaffingPageKeys, "WEEKLY" | "MONTHLY">;
	currentDailyDate?: never;
};
/**
 * People projects a rotation cycle onto real calendar weeks, so it carries the SAME date apparatus as
 * the other views — Today, the range text, both arrows and the picker — and the date props are
 * required here rather than `never` (they were `never` while the roster was wrongly believed to have
 * no date dimension; it visibly shows dated week columns).
 *
 * `currentDate` and `visibleRangeEnd` are both required because People's window is LOCAL to that view:
 * this component cannot derive it from the shared `weekIndex` the way it derives Monthly's. That
 * `weekIndex` is still passed, unchanged, purely to anchor the date-based view the user switches back
 * to — People never writes it.
 */
type PeopleProps = BaseProps & {
	initialPageType: Extract<StaffingPageKeys, "PEOPLE">;
	weekIndex: number;
	setWeekIndex: (index: number) => void;
	currentDailyDate?: never;
	currentDate: DateTime;
	visibleRangeEnd: DateTime;
};

export const StaffingDateTypeSelector: FC<DailyProps | WeeklyOrMonthlyProps | PeopleProps> = ({
	currentDateText,
	onResetButtonClick,
	onPreviousClick,
	onNextClick,
	initialPageType,
	weekIndex,
	setWeekIndex,
	currentDailyDate,
	isPrevDisabled = false,
	isNextDisabled = false,
	currentDate,
	onDateSelected,
	minDate,
	maxDate,
	visibleRangeEnd,
	"data-annie-target": annieTarget,
}) => {
	const { hospitalData, timezone, getWeekIndexByDate, chosenDepartment, isMedicalDepartment } = useHospitalData();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const weekStartDay = useEffectiveWeekStartDay();
	const offset = getVisibleWeekOffset(weekStartDay);

	// Offer Month and People only where they are actually reachable, latched per department so an option
	// can't flicker in and out while the department hydrates (see `useLatchedViewOffer`). Each option is
	// gated on the very predicate its route guard consults, so an offer can never become a redirect.
	const departmentId = chosenDepartment.department_id;
	const monthlyViewAvailability = useMonthlyViewAvailable();
	const peopleViewAvailability = useStaffingPeopleViewAvailable();
	const isMonthlyViewOffered = useLatchedViewOffer(monthlyViewAvailability, departmentId);
	const isPeopleViewOffered = useLatchedViewOffer(peopleViewAvailability, departmentId);

	// The People roster is the one option with a translated label: Day/Week/Month are the enum's own
	// display strings, and re-keying those is a separate change.
	const staffingTypeDropdownOptions = useMemo(
		() => enumToDropdownItems(StaffingPageType, (value) => (value === StaffingPageType.PEOPLE ? t("staffing.area.view.people") : value)),
		[t],
	);

	// The first day VISIBLE for the current week index. `weeklyShifts[0].days[0]` is always Sunday no matter
	// what the department configured, so a Monday-start department was reading (and navigating to) a date up
	// to six days before the window actually on screen.
	// DEFAULT: the `??` covers the one case with no answer to read — `hospitalData.weeks` not yet loaded.
	// It resolves in the DEPARTMENT's zone, not the browser's: a manager east of the facility crosses into
	// tomorrow first, and the browser-zone version then serialized a date one day ahead of the department's
	// — the wrong week entirely on a week boundary. `timezone` is in the deps for the same reason.
	const firstDateInCurrentWeekIndex = useMemo(
		() =>
			hospitalData.weeks[weekIndex ?? 0]?.weeklyShifts.at(0)?.days.at(0)?.date?.plus({ days: offset }) ??
			getWeekStart(DateTime.now().setZone(timezone), weekStartDay),
		[weekIndex, hospitalData, offset, weekStartDay, timezone],
	);
	// The last day actually visible in the Monthly view: `MONTHLY_WEEKS_TO_SHOW` weeks from the window's
	// first visible day, minus a day (inclusive last day). Derived from the constant, so it stays exactly the
	// span on screen — which is now genuinely six weeks. Dates past the window must not read as "in view",
	// or switching Month→Daily/Weekly would jump to today when today isn't on screen; in that case the range
	// deliberately falls back to the window's first visible day instead.
	const lastDateInMonthlyView = useMemo(
		() => firstDateInCurrentWeekIndex.plus({ weeks: MONTHLY_WEEKS_TO_SHOW }).minus({ days: 1 }),
		[firstDateInCurrentWeekIndex],
	);
	const [pageType, setPageType] = useState<StaffingPageKeys>(initialPageType);
	const [datePickerAnchorEl, setDatePickerAnchorEl] = useState<HTMLElement | null>(null);

	const toggleDatePicker = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			if (!onDateSelected) {
				return;
			}

			// The anchor is read (and the click tracked) OUT here rather than inside the state updater.
			// React only evaluates an updater eagerly while the fiber has no pending work; otherwise it
			// defers it to the render phase, by which point `currentTarget` has been cleared — so the
			// picker opened on the SECOND click instead of the first. An updater must also stay free of
			// side effects, which the `trackEvent` call inside it was not.
			const nextAnchor = datePickerAnchorEl ? null : event.currentTarget;
			if (nextAnchor) {
				trackEvent("staffing_date_text_clicked", { page_type: initialPageType });
			}
			setDatePickerAnchorEl(nextAnchor);
		},
		[onDateSelected, initialPageType, datePickerAnchorEl],
	);

	const handlePickerDateChange = useCallback(
		(newDate: DateTime | null) => {
			if (newDate?.isValid && onDateSelected) {
				onDateSelected(newDate);
				trackEvent("staffing_date_picker_date_selected", {
					selected_date: newDate.toFormat("yyyy-MM-dd"),
					page_type: initialPageType,
				});
			}
			setDatePickerAnchorEl(null);
		},
		[onDateSelected, initialPageType],
	);

	const closeDatePicker = useCallback(() => {
		setDatePickerAnchorEl(null);
	}, []);

	// Leaving a MULTI-WEEK view (Monthly's six-week window, People's rotation projection) for one that
	// needs a single date: anchor on today when today is actually inside the window on screen, otherwise
	// on that window's own start, so the user lands where they were looking. `windowStart` is passed in
	// rather than read from the shared week cursor because People's window is local to that view.
	const navigateAwayFromWindowView = useCallback(
		(item: StaffingPageKeys, isTodayInView: boolean, windowStart: DateTime) => {
			const anchorDate = isTodayInView ? DateTime.now().setZone(timezone) : windowStart;
			if (item === "DAILY") {
				navigate(daySchedulePath(chosenDepartment.department_id, anchorDate.toFormat("yyyy-MM-dd")));
			} else if (item === "WEEKLY") {
				const targetWeekIndex = getWeekIndexByDate(anchorDate);
				// `-1` means the anchor's week was never loaded. Writing it would corrupt the week cursor the
				// weekly and monthly grids read (they index `hospitalData.weeks` with it), so leave the shared
				// cursor alone and let the weekly grid open on the week it already had.
				if (targetWeekIndex !== -1) setWeekIndex(targetWeekIndex);
				navigate(staffingWeeklyPath(chosenDepartment.department_id));
			} else if (item === "MONTHLY") {
				// The Monthly URL names the VISIBLE window start, which is what this anchor already is.
				navigate(staffingMonthlyPath(chosenDepartment.department_id, windowStart.toFormat("yyyy-MM-dd")));
			}
		},
		[navigate, timezone, getWeekIndexByDate, setWeekIndex, chosenDepartment.department_id],
	);

	const handlePageTypeChange = useCallback(
		(item?: StaffingPageKeys) => {
			if (item && item !== initialPageType) {
				setPageType(item);
				trackEvent("staffing_page_type_changed", { new_page_type: item });
				const today = DateTime.now().setZone(timezone);
				// People is a route with no date in it, so every view reaches it the same way.
				if (item === "PEOPLE") {
					navigate(staffingPeoplePath(chosenDepartment.department_id));
					return;
				}
				if (initialPageType === "PEOPLE" || initialPageType === "MONTHLY") {
					// Both multi-week views hand over the window they actually have on screen. Monthly's is the
					// six-week window derived from the shared cursor; People's is its own projection window,
					// which only that view knows — hence `currentDate` / `visibleRangeEnd` on `PeopleProps`.
					// DEFAULT: the fallbacks cover a caller that omitted them. `PeopleProps` makes that a type
					// error for People, so in practice they only ever supply Monthly's own derived window.
					const windowStart = currentDate ?? firstDateInCurrentWeekIndex;
					const windowEnd = visibleRangeEnd ?? lastDateInMonthlyView;
					const currentDateIsInView = today >= windowStart.startOf("day") && today <= windowEnd.endOf("day");
					navigateAwayFromWindowView(item, currentDateIsInView, windowStart);
				} else if (initialPageType === "WEEKLY") {
					if (item === "DAILY") {
						const isTodayCurrentWeek = getWeekIndexByDate(today) === weekIndex;
						const date = isTodayCurrentWeek ? today.toFormat("yyyy-MM-dd") : firstDateInCurrentWeekIndex.toFormat("yyyy-MM-dd");
						navigate(daySchedulePath(chosenDepartment.department_id, date));
					} else if (item === "MONTHLY") {
						navigate(staffingMonthlyPath(chosenDepartment.department_id, firstDateInCurrentWeekIndex.toFormat("yyyy-MM-dd")));
					}
				} else if (initialPageType === "DAILY") {
					const calculatedWeekIndex = getWeekIndexByDate(currentDailyDate);
					if (item === "WEEKLY") {
						setWeekIndex(calculatedWeekIndex);
						navigate(staffingWeeklyPath(chosenDepartment.department_id));
					} else if (item === "MONTHLY") {
						// The Monthly URL names the VISIBLE window start, so shift the Sunday-indexed anchor by the
						// department's week-start offset before handing it over.
						const firstDateOfWeek = hospitalData.weeks[calculatedWeekIndex]?.weeklyShifts.at(0)?.days.at(0)?.date?.plus({ days: offset });
						if (!firstDateOfWeek) {
							trackErrorEvent("staffing_date_type_selector", "First date of week not found", {
								calculatedWeekIndex,
								currentDailyDate: currentDailyDate?.toFormat("yyyy-MM-dd"),
							});
						} else {
							navigate(staffingMonthlyPath(chosenDepartment.department_id, firstDateOfWeek.toFormat("yyyy-MM-dd")));
						}
					}
				}
			}
		},
		[
			initialPageType,
			navigate,
			navigateAwayFromWindowView,
			currentDate,
			visibleRangeEnd,
			firstDateInCurrentWeekIndex,
			lastDateInMonthlyView,
			getWeekIndexByDate,
			setWeekIndex,
			currentDailyDate,
			weekIndex,
			timezone,
			hospitalData,
			chosenDepartment,
			offset,
		],
	);

	return (
		<Box
			data-testid={"staffing-date-type-selector"}
			data-annie-target={annieTarget}
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				width: "fit-content",
				height: "fit-content",
				gap: 1,
			}}
		>
			<SystemButton
				data-testid="staffing-date-type-today-button"
				onClick={onResetButtonClick}
				buttonVariant="secondary"
				sx={{
					...headerTypographySx,
					fontWeight: 700,
					fontSize: "16px",
					color: mainElementsColor,
					borderColor: mainElementsColor,
					borderWidth: "1px",
					backgroundColor: "transparent",
					p: "3px",
					borderRadius: BADGE.PILL_RADIUS_FULL,
					minWidth: "80px",
				}}
			>
				Today
			</SystemButton>
			<Box sx={{ display: "flex", alignItems: "center" }}>
				<Typography
					data-testid="staffing-date-text"
					onClick={onDateSelected ? toggleDatePicker : undefined}
					aria-label={onDateSelected ? "Select date" : undefined}
					sx={{
						...headerTypographySx,
						fontWeight: 500,
						fontSize: "16px",
						minWidth: "120px",
						textAlign: "center",
						paddingX: "10px",
						...(onDateSelected && {
							cursor: "pointer",
							textDecoration: "underline",
						}),
					}}
				>
					{currentDateText}
				</Typography>
				<IconButton data-testid={"staffing-date-type-selector-previous-button"} size={"small"} onClick={onPreviousClick} disabled={isPrevDisabled}>
					<ArrowBackIosIcon fontSize={"inherit"} style={{ color: isPrevDisabled ? INK.DISABLED : mainElementsColor }} />
				</IconButton>
				<IconButton data-testid={"staffing-date-type-selector-next-button"} size={"small"} onClick={onNextClick} disabled={isNextDisabled}>
					<ArrowForwardIosIcon fontSize={"inherit"} style={{ color: isNextDisabled ? INK.DISABLED : mainElementsColor }} />
				</IconButton>
			</Box>
			<SystemDropdown
				data-testid="staffing-date-type-dropdown"
				key={"staffing-date-type-selector-dropdown"}
				items={
					isMedicalDepartment
						? // Medical departments run the synergy grid: Week is the only view they ever offered, and the
							// People roster was never reachable there either (their header hosts no settings panel).
							staffingTypeDropdownOptions.filter((value) => value.value === "WEEKLY")
						: // Month and People are offered only where they're actually reachable (flags on, department
							// resolved, and for Month enabled jobs); otherwise the dropdown keeps the historical
							// Day/Week shape so an option can't bounce straight back to weekly.
							staffingTypeDropdownOptions.filter(
								(value) => (value.value !== "MONTHLY" || isMonthlyViewOffered) && (value.value !== "PEOPLE" || isPeopleViewOffered),
							)
				}
				selectedItem={pageType}
				onChange={handlePageTypeChange}
				selectSx={{ minWidth: "80px" }}
			/>
			{onDateSelected && datePickerAnchorEl && (
				<SystemDatePickerPopup
					data-testid="staffing-date-picker-popup"
					overridePickerProps={{ ...(minDate && { minDate }), ...(maxDate && { maxDate }) }}
					value={currentDate ?? null}
					onChange={handlePickerDateChange}
					anchorEl={datePickerAnchorEl}
					open={!!datePickerAnchorEl}
					onClose={closeDatePicker}
				/>
			)}
		</Box>
	);
};
